import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import './PostpartumSection.css';

function PostpartumOverview() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [postpartumInfo, setPostpartumInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const infoData = await api.getPostpartumInfo();
      setPostpartumInfo(infoData);
    } catch (error) {
      console.error('Error loading postpartum overview:', error);
      setError(t('postpartum.error.load', 'Unable to load postpartum data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="postpartum-section">
        <LoadingSpinner message={t('postpartum.loading', 'Loading your postpartum information...')} />
      </div>
    );
  }

  return (
    <div className="postpartum-section">
      <div className="section-header">
        <button className="btn-back" onClick={() => navigate(`/postpartum/${userId}`)}>
          ← {t('action.back', 'Back')}
        </button>
        <h1>📊 {t('postpartum.title.overview', 'Postpartum Overview')}</h1>
        <nav className="postpartum-nav">
          <Link to={`/postpartum/${userId}/overview`} className="nav-link active">{t('postpartum.nav.overview', 'Overview')}</Link>
          <Link to={`/postpartum/${userId}/recovery`} className="nav-link">{t('postpartum.nav.recovery', 'Recovery')}</Link>
          <Link to={`/postpartum/${userId}/nutrition`} className="nav-link">{t('postpartum.nav.nutrition', 'Nutrition')}</Link>
          <Link to={`/postpartum/${userId}/appointments`} className="nav-link">{t('postpartum.nav.appointments', 'Appointments')}</Link>
          <Link to={`/postpartum/${userId}/warnings`} className="nav-link">{t('postpartum.nav.warnings', 'Warnings')}</Link>
        </nav>
      </div>

      <div className="section-content">
        {error && <div className="postpartum-error">{error}</div>}
        {postpartumInfo?.postpartumPeriods && (
          <section className="data-section periods-section">
            <h2>📅 {t('postpartum.periods', 'Postpartum Periods')}</h2>
            <div className="nutrient-foods-grid">
              {postpartumInfo.postpartumPeriods.map((period, i) => (
                <div key={i} className="nutrient-category">
                  <h3>{period.period}</h3>
                  <p style={{marginBottom: '10px', fontWeight: 'bold'}}>{period.description}</p>
                  <p style={{color: '#666'}}>{period.focus}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {postpartumInfo?.mentalHealth && (
          <section className="data-section mental-health-section">
            <h2>🧘 {t('postpartum.mental.health', 'Mental Health')}</h2>
            <div className="nutrient-foods-grid">
              {postpartumInfo.mentalHealth.babyBlues && (
                <div className="nutrient-category">
                  <h3>😊 {t('postpartum.baby.blues', 'Baby Blues')}</h3>
                  <p style={{marginBottom: '10px'}}>{postpartumInfo.mentalHealth.babyBlues.description}</p>
                  <div style={{marginBottom: '15px'}}>
                    <strong style={{display: 'block', marginBottom: '8px'}}>{t('postpartum.label.symptoms', 'Symptoms')}:</strong>
                    <ul>
                      {postpartumInfo.mentalHealth.babyBlues.symptoms?.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <p style={{fontSize: '0.9rem', color: '#666'}}>
                    <strong>{t('postpartum.label.duration', 'Duration')}:</strong> {postpartumInfo.mentalHealth.babyBlues.duration}
                  </p>
                </div>
              )}

              {postpartumInfo.mentalHealth.postpartumDepression && (
                <div className="nutrient-category nutrient-category-warning">
                  <h3>⚠️ {t('postpartum.depression', 'Postpartum Depression')}</h3>
                  <p style={{marginBottom: '10px'}}>{postpartumInfo.mentalHealth.postpartumDepression.description}</p>
                  <div style={{marginBottom: '15px'}}>
                    <strong style={{display: 'block', marginBottom: '8px'}}>{t('postpartum.label.symptoms', 'Symptoms')}:</strong>
                    <ul>
                      {postpartumInfo.mentalHealth.postpartumDepression.symptoms?.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <p style={{fontSize: '0.9rem', color: '#c62828', fontWeight: 'bold', marginBottom: '10px'}}>
                    {postpartumInfo.mentalHealth.postpartumDepression.whenToSeekHelp}
                  </p>
                  {postpartumInfo.mentalHealth.postpartumDepression.treatment && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px'}}>{t('postpartum.label.treatmentOptions', 'Treatment Options')}:</strong>
                      <ul>
                        {postpartumInfo.mentalHealth.postpartumDepression.treatment.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {postpartumInfo.mentalHealth.postpartumAnxiety && (
                <div className="nutrient-category nutrient-category-caution">
                  <h3>😰 {t('postpartum.anxiety', 'Postpartum Anxiety')}</h3>
                  <div style={{marginBottom: '15px'}}>
                    <strong style={{display: 'block', marginBottom: '8px'}}>{t('postpartum.label.symptoms', 'Symptoms')}:</strong>
                    <ul>
                      {postpartumInfo.mentalHealth.postpartumAnxiety.symptoms?.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  {postpartumInfo.mentalHealth.postpartumAnxiety.support && (
                    <p style={{fontSize: '0.9rem', color: '#666'}}>
                      <strong>{t('postpartum.label.support', 'Support')}:</strong> {postpartumInfo.mentalHealth.postpartumAnxiety.support}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {postpartumInfo?.breastfeeding && (
          <section className="data-section breastfeeding-section">
            <h2>🤱 {t('postpartum.breastfeeding', 'Breastfeeding Support')}</h2>
            <div className="nutrient-foods-grid">
              {postpartumInfo.breastfeeding.firstWeek && (
                <>
                  {postpartumInfo.breastfeeding.firstWeek.day1to3 && (
                    <div className="nutrient-category">
                      <h3>📅 {t('postpartum.breastfeeding.days.1to3', 'Days 1-3')}</h3>
                      <ul>
                        {postpartumInfo.breastfeeding.firstWeek.day1to3.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {postpartumInfo.breastfeeding.firstWeek.day3to5 && (
                    <div className="nutrient-category">
                      <h3>📅 {t('postpartum.breastfeeding.days.3to5', 'Days 3-5')}</h3>
                      <ul>
                        {postpartumInfo.breastfeeding.firstWeek.day3to5.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              {postpartumInfo.breastfeeding.support && postpartumInfo.breastfeeding.support.length > 0 && (
                <div className="nutrient-category">
                  <h3>💡 {t('postpartum.breastfeeding.support', 'Support Tips')}</h3>
                  <ul>
                    {postpartumInfo.breastfeeding.support.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {postpartumInfo.breastfeeding.commonIssues && postpartumInfo.breastfeeding.commonIssues.length > 0 && (
                <div className="nutrient-category nutrient-category-caution">
                  <h3>⚠️ {t('postpartum.breastfeeding.common.issues', 'Common Issues')}</h3>
                  <ul>
                    {postpartumInfo.breastfeeding.commonIssues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              {postpartumInfo.breastfeeding.whenToCall && postpartumInfo.breastfeeding.whenToCall.length > 0 && (
                <div className="nutrient-category nutrient-category-warning">
                  <h3>📞 {t('postpartum.breastfeeding.when.to.call', 'When to Call Your Doctor')}</h3>
                  <ul>
                    {postpartumInfo.breastfeeding.whenToCall.map((item, i) => (
                      <li key={i} style={{color: '#c62828'}}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {postpartumInfo?.contraception && (
          <section className="data-section contraception-section">
            <h2>💊 {t('postpartum.contraception', 'Contraception & Family Planning')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>⏰ {t('postpartum.contraception.timing', 'Timing')}</h3>
                <p style={{marginBottom: '15px'}}>{postpartumInfo.contraception.timing}</p>
                {postpartumInfo.contraception.options && postpartumInfo.contraception.options.length > 0 && (
                  <div>
                    {postpartumInfo.contraception.options.map((option, i) => (
                      <div key={i} style={{marginBottom: '15px', paddingBottom: '15px', borderBottom: i < postpartumInfo.contraception.options.length - 1 ? '1px solid #e0e0e0' : 'none'}}>
                        <strong style={{display: 'block', marginBottom: '8px', color: '#667eea'}}>{option.type}:</strong>
                        <ul>
                          {option.options?.map((opt, j) => (
                            <li key={j}>{opt}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {postpartumInfo.contraception.breastfeeding && (
                <div className="nutrient-category">
                  <h3>🤱 {t('postpartum.contraception.breastfeeding', 'Breastfeeding Considerations')}</h3>
                  <p>{postpartumInfo.contraception.breastfeeding}</p>
                </div>
              )}
              {postpartumInfo.contraception.considerations && postpartumInfo.contraception.considerations.length > 0 && (
                <div className="nutrient-category">
                  <h3>💭 {t('postpartum.contraception.considerations', 'Things to Consider')}</h3>
                  <ul>
                    {postpartumInfo.contraception.considerations.map((consideration, i) => (
                      <li key={i}>{consideration}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {postpartumInfo?.returnToWork && (
          <section className="data-section return-to-work-section">
            <h2>💼 {t('postpartum.return.to.work', 'Returning to Work')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>⏰ {t('postpartum.return.to.work.timing', 'Timing')}</h3>
                <p style={{marginBottom: '15px'}}>{postpartumInfo.returnToWork.timing}</p>
                {postpartumInfo.returnToWork.considerations && postpartumInfo.returnToWork.considerations.length > 0 && (
                  <div>
                    <strong style={{display: 'block', marginBottom: '8px'}}>Considerations:</strong>
                    <ul>
                      {postpartumInfo.returnToWork.considerations.map((consideration, i) => (
                        <li key={i}>{consideration}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {postpartumInfo.returnToWork.breastfeeding && postpartumInfo.returnToWork.breastfeeding.length > 0 && (
                <div className="nutrient-category">
                  <h3>🤱 {t('postpartum.return.to.work.breastfeeding', "If You're Breastfeeding")}</h3>
                  <ul>
                    {postpartumInfo.returnToWork.breastfeeding.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {postpartumInfo?.sleep && (
          <section className="data-section sleep-section">
            <h2>😴 {t('postpartum.sleep', 'Sleep & Rest')}</h2>
            <div className="nutrient-foods-grid">
              {postpartumInfo.sleep.challenges && postpartumInfo.sleep.challenges.length > 0 && (
                <div className="nutrient-category nutrient-category-caution">
                  <h3>😴 {t('postpartum.sleep.challenges', 'Sleep Challenges')}</h3>
                  <ul>
                    {postpartumInfo.sleep.challenges.map((challenge, i) => (
                      <li key={i}>{challenge}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="nutrient-category">
                <h3>💤 {t('postpartum.sleep.tips', 'Sleep Tips')}</h3>
                <ul>
                  {postpartumInfo.sleep.tips?.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
                <p style={{marginTop: '15px', fontSize: '0.9rem', color: '#666'}}>
                  <strong>{postpartumInfo.sleep.importance}</strong>
                </p>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default PostpartumOverview;

